import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Minus, Plus, Trash2, ShoppingCart, Store, Truck, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const navigate = useNavigate();
  const { items, updateQty, removeItem, clearFarm } = useCart();
  const { user } = useAuth();
  const [fulfilment, setFulfilment] = useState({});
  const [dates, setDates] = useState({});
  const [addresses, setAddresses] = useState({});
  const [placing, setPlacing] = useState({});

  const setAddr = (farmId, field, value) =>
    setAddresses((p) => ({ ...p, [farmId]: { ...(p[farmId] || {}), [field]: value } }));

  const groups = useMemo(() => {
    const map = {};
    items.forEach((i) => {
      if (!map[i.farm.id]) map[i.farm.id] = { farm: i.farm, items: [] };
      map[i.farm.id].items.push(i);
    });
    return Object.values(map);
  }, [items]);

  const getFul = (g) => fulfilment[g.farm.id] || (g.farm.collection_available ? "collection" : "delivery");

  const subtotal = (g) => g.items.reduce((s, i) => s + i.product.price * i.quantity, 0);
  const deliveryFee = (g) => getFul(g) === "delivery" ? (Number(g.farm.delivery_fee) || 0) : 0;

  const placeOrder = async (g) => {
    if (!user) { toast.error("Please sign in to place your order"); navigate("/auth", { state: { from: "/cart" } }); return; }
    const date = dates[g.farm.id];
    if (!date) { toast.error("Please pick a fulfilment date"); return; }
    const ful = getFul(g);
    const addr = addresses[g.farm.id] || {};
    if (ful === "delivery" && (!addr.line1?.trim() || !addr.postcode?.trim())) {
      toast.error("Please enter a delivery address and postcode");
      return;
    }
    setPlacing((p) => ({ ...p, [g.farm.id]: true }));
    try {
      await api.post("/orders", {
        farm_id: g.farm.id,
        fulfilment_type: ful,
        fulfilment_date: date,
        items: g.items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        delivery_address_line1: ful === "delivery" ? addr.line1 : "",
        delivery_address_line2: ful === "delivery" ? (addr.line2 || "") : "",
        delivery_town: ful === "delivery" ? (addr.town || "") : "",
        delivery_postcode: ful === "delivery" ? addr.postcode : "",
      });
      toast.success(`Pre-order placed with ${g.farm.farm_name}!`);
      clearFarm(g.farm.id);
      navigate("/my-orders");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not place order");
    } finally { setPlacing((p) => ({ ...p, [g.farm.id]: false })); }
  };

  if (items.length === 0) {
    return (
      <div className="pt-16 min-h-screen grid place-items-center px-4">
        <Card className="rounded-2xl p-10 text-center max-w-md" data-testid="cart-empty">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3"><ShoppingCart className="h-7 w-7 text-primary" /></div>
          <h2 className="text-xl font-semibold">Your cart is empty</h2>
          <p className="text-muted-foreground text-sm mt-1">Discover local food producers and add fresh produce.</p>
          <Button className="mt-4" onClick={() => navigate("/map")}>Explore the map</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen" data-testid="cart-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <h1 className="text-2xl font-semibold mb-1">Your pre-order</h1>
        <p className="text-muted-foreground text-sm mb-5 flex items-center gap-1"><Info className="h-4 w-4" /> One order per producer · pay on collection or delivery.</p>

        <Accordion type="multiple" defaultValue={groups.map((g) => g.farm.id)} className="space-y-4">
          {groups.map((g) => (
            <AccordionItem key={g.farm.id} value={g.farm.id} className="border-0">
              <Card className="rounded-2xl overflow-hidden">
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <div className="flex items-center gap-2"><Store className="h-4 w-4 text-primary" /><span className="font-semibold">{g.farm.farm_name}</span><Badge variant="secondary">{g.items.length} item{g.items.length !== 1 ? "s" : ""}</Badge></div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-3">
                    {g.items.map((i) => (
                      <div key={i.product.id} className="flex items-center gap-3">
                        {i.product.image_url ? <img src={imgUrl(i.product.image_url)} className="h-14 w-14 rounded-lg object-cover" alt="" /> : <div className="h-14 w-14 rounded-lg bg-secondary" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{i.product.name}</div>
                          <div className="text-sm text-muted-foreground">£{i.product.price.toFixed(2)} {i.product.unit}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => updateQty(i.product.id, i.quantity - 1)} data-testid="cart-qty-minus"><Minus className="h-3 w-3" /></Button>
                          <span className="w-8 text-center font-medium" data-testid="cart-item-qty">{i.quantity}</span>
                          <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => updateQty(i.product.id, i.quantity + 1)} data-testid="cart-qty-plus"><Plus className="h-3 w-3" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeItem(i.product.id)} data-testid="cart-remove-item"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                        <div className="w-16 text-right font-medium">£{(i.product.price * i.quantity).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>

                  <Separator className="my-4" />

                  <div className="space-y-3">
                    <div>
                      <Label className="mb-1.5 block">Fulfilment</Label>
                      <RadioGroup value={getFul(g)} onValueChange={(v) => setFulfilment((p) => ({ ...p, [g.farm.id]: v }))} className="flex gap-2">
                        {g.farm.collection_available && (
                          <label className={`flex-1 flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer text-sm ${getFul(g) === 'collection' ? 'border-primary bg-secondary' : 'border-border'}`}>
                            <RadioGroupItem value="collection" data-testid="cart-fulfilment-collection" /><Store className="h-4 w-4" />Collection</label>
                        )}
                        {g.farm.delivery_available && (
                          <label className={`flex-1 flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer text-sm ${getFul(g) === 'delivery' ? 'border-primary bg-secondary' : 'border-border'}`}>
                            <RadioGroupItem value="delivery" data-testid="cart-fulfilment-delivery" /><Truck className="h-4 w-4" />Delivery{g.farm.delivery_fee ? ` (£${Number(g.farm.delivery_fee).toFixed(2)})` : ""}</label>
                        )}
                      </RadioGroup>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Preferred {getFul(g)} date</Label>
                      <Input type="date" value={dates[g.farm.id] || ""} min={new Date().toISOString().split("T")[0]}
                        onChange={(e) => setDates((p) => ({ ...p, [g.farm.id]: e.target.value }))} data-testid="checkout-fulfilment-date" />
                    </div>
                    {getFul(g) === "delivery" && (
                      <div className="space-y-2 rounded-lg border border-border p-3" data-testid="checkout-delivery-address">
                        <Label className="flex items-center gap-1"><Truck className="h-4 w-4" /> Delivery address</Label>
                        <Input placeholder="Address line 1 *" value={(addresses[g.farm.id]?.line1) || ""}
                          onChange={(e) => setAddr(g.farm.id, "line1", e.target.value)} data-testid="delivery-line1" />
                        <Input placeholder="Address line 2 (optional)" value={(addresses[g.farm.id]?.line2) || ""}
                          onChange={(e) => setAddr(g.farm.id, "line2", e.target.value)} data-testid="delivery-line2" />
                        <div className="grid grid-cols-2 gap-2">
                          <Input placeholder="Town/City" value={(addresses[g.farm.id]?.town) || ""}
                            onChange={(e) => setAddr(g.farm.id, "town", e.target.value)} data-testid="delivery-town" />
                          <Input placeholder="Postcode *" value={(addresses[g.farm.id]?.postcode) || ""}
                            onChange={(e) => setAddr(g.farm.id, "postcode", e.target.value)} data-testid="delivery-postcode" />
                        </div>
                        <p className="text-xs text-muted-foreground">Delivery available within {g.farm.delivery_radius_miles || 0} miles of the producer.</p>
                      </div>
                    )}
                    <div className="rounded-lg bg-secondary/50 p-3 text-sm space-y-1">
                      <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>£{subtotal(g).toFixed(2)}</span></div>
                      {deliveryFee(g) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Delivery fee</span><span>£{deliveryFee(g).toFixed(2)}</span></div>}
                      <div className="flex justify-between font-semibold text-base pt-1"><span>Total</span><span data-testid="cart-farm-total">£{(subtotal(g) + deliveryFee(g)).toFixed(2)}</span></div>
                    </div>
                    <Button className="w-full bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" onClick={() => placeOrder(g)} disabled={placing[g.farm.id]} data-testid="checkout-place-preorder-button">
                      {placing[g.farm.id] ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Place pre-order
                    </Button>
                  </div>
                </AccordionContent>
              </Card>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}
